import React, { createContext, useCallback, useState, useEffect } from 'react';
import { fetchWithAuth, setLogoutCallback } from './utils/fetchWithAuth';

export const AuthContext = createContext();
const API_URL = process.env.REACT_APP_API_URL;

export const AuthProvider = ({ children }) => {
  const [auth, setAuth] = useState(() => {
    const token = localStorage.getItem("token");
    return { token, user: null, role: null, locataire_id: null, mustChangePassword: false, token_received_at: null };
  });

  const [loading, setLoading] = useState(true);
  const [globalError, setGlobalError] = useState(null);

  const fetchUser = useCallback(async (token) => {
    if (!token) {
      setAuth({ token: null, user: null, role: null, locataire_id: null, mustChangePassword: false, token_received_at: null });
      setLoading(false);
      return;
    }
    try {
      const response = await fetchWithAuth(`${API_URL}/api/me/`);
      if (response.ok) {
        const data = await response.json();
        const mainRole = data.roles?.includes("admins")
          ? "admin"
          : data.roles?.includes("locataires")
          ? "locataire"
          : "autre";
        setAuth(authPrev => ({
          ...authPrev,
          token,
          user: data,
          role: mainRole,
          locataire_id: data.locataire_id || null,
          mustChangePassword: data.must_change_password || false,
        }));
        localStorage.setItem("role", mainRole);
        if (data.locataire_id) {
          localStorage.setItem("locataire_id", data.locataire_id);
        } else {
          localStorage.removeItem("locataire_id");
        }
      } else {
        logout();
      }
    } catch (err) {
      logout();
    }
    setLoading(false);
  }, []);

  const login = async (username, password) => {
    try {
      const response = await fetch(`${API_URL}/api/token/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password })
      });
      if (response.ok) {
        const data = await response.json();
        localStorage.setItem("token", data.access);
        localStorage.setItem("refresh", data.refresh);
        localStorage.setItem('token_received_at', Date.now().toString());
        await fetchUser(data.access);
        return true;
      } else if (response.status === 401 || response.status === 403) {
        setLoading(false);
        return false;
      } else {
        setLoading(false);
        throw new Error("Erreur de connexion au serveur. Essayez plus tard");
      }
    } catch (e) {
      setLoading(false);
      throw new Error("Erreur de connexion. Vérifiez votre réseau ou réessayez plus tard");
    }
  };

  const logout = () => {
    setAuth({ token: null, user: null, role: null, locataire_id: null, token_received_at: null});
    localStorage.removeItem("token");
    localStorage.removeItem("refresh");
    localStorage.removeItem("role");
    localStorage.removeItem("locataire_id");
    localStorage.removeItem("token_received_at");
  };

  const refreshUser = () => {
    const token = localStorage.getItem("token");
    if (token) {
      setLoading(true);
      fetchUser(token);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem("token");
    setLogoutCallback(logout);
    if (token) {
      fetchUser(token);
    } else {
      setLoading(false);
    }
  }, [fetchUser]);


  return (
    <AuthContext.Provider value={{
      ...auth,
      isAdmin: auth.role === "admin",
      isLocataire: auth.role === "locataire",
      loading,
      login, logout, refreshUser,
      globalError, setGlobalError
    }}>
      {children}
    </AuthContext.Provider>
  );
};